import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { loadDevI18nResources } from './devResources'
import { createLazyLocaleBackend, SUPPORTED_LANGUAGES } from './lazyResources'
import { PUBLIC_NAMESPACES, publicEnResources } from './publicResources'
import { ENV } from '../config/env'
import { getBrowserAdapter } from '../lib/browserAdapter'

// Gate verbose i18n logging behind VITE_I18N_DEBUG env var
const I18N_DEBUG = ENV.I18N_DEBUG === 'true'

// Parse fallback language from environment variable
const parseFallbackLng = (): string | string[] => {
    const fallbackLng = ENV.I18N_FALLBACK_LNG
    if (!fallbackLng) {
        return 'en'
    }
    // Support comma-separated array format: "de,en" -> ["de", "en"]
    if (fallbackLng.includes(',')) {
        return fallbackLng.split(',').map((lng) => lng.trim())
    }
    return fallbackLng
}

// Update HTML lang attribute when locale changes
const updateHtmlLang = (lng: string) => {
    getBrowserAdapter().setDocumentLang(lng)
}

// Initialize i18n based on environment
// Check if we're in test mode (tests use createTestI18n, but this prevents eager loading if main i18n is imported)
const isTestMode =
    typeof process !== 'undefined' &&
    (process.env.NODE_ENV === 'test' || process.env.VITEST === 'true')

// import.meta.env.DEV (not ENV.MODE) so Vite statically eliminates this branch
// in production builds — otherwise the eager locale glob below drags every
// language's JSON into the public bundle (TRA-115).
if (import.meta.env.DEV && !isTestMode) {
    // Development: Use eager imports for HMR support
    // Skip eager loading in test mode (tests use createTestI18n with lazy loading)
    const devRes = loadDevI18nResources()

    if (I18N_DEBUG) {
        console.log('i18n: Dev resources loaded:', devRes)
    }

    i18n.use(LanguageDetector)
        .use(initReactI18next)
        .init({
            // Type assertion needed: devRes is dynamically loaded and i18next types are complex
            resources: devRes as Parameters<typeof i18n.init>[0]['resources'],
            fallbackLng: parseFallbackLng(),
            defaultNS: 'common',
            fallbackNS: ['common'],
            detection: {
                order: ['localStorage', 'navigator', 'querystring', 'htmlTag'],
                caches: ['localStorage'],
            },
            interpolation: {
                escapeValue: false,
            },
            debug: I18N_DEBUG, // Enable verbose i18n logging via VITE_I18N_DEBUG=true
        })
        .then(() => {
            // Set initial HTML lang attribute
            updateHtmlLang(i18n.language)

            // Listen for language changes and update HTML lang attribute
            i18n.on('languageChanged', (lng) => {
                updateHtmlLang(lng)
            })

            if (I18N_DEBUG) {
                console.log('i18n: Initialization complete')
                console.log('i18n: Detected language:', i18n.language)
                console.log(
                    'i18n: Available namespaces:',
                    Object.keys(i18n.services.resourceStore.data[i18n.language] || {})
                )
            }
        })
        .catch((error) => {
            console.error('i18n: Initialization failed:', error)
        })

    // Enable hot reloading for i18n in development
    const hotReload = (
        import.meta as ImportMeta & { hot?: { accept: (dep: string, cb: () => void) => void } }
    ).hot
    if (hotReload) {
        hotReload.accept('./devResources', () => {
            if (I18N_DEBUG) {
                console.log('i18n: Hot reloading translations...')
            }
            const res = loadDevI18nResources()
            // Iterate over all locales (en, de, etc.)
            for (const [lang, namespaces] of Object.entries(res)) {
                for (const [ns, bundle] of Object.entries(namespaces)) {
                    i18n.addResourceBundle(lang, ns, bundle, true, true)
                }
            }
            if (I18N_DEBUG) {
                console.log('i18n: Translations reloaded')
            }
        })
    }
} else {
    // Production: locale namespaces load lazily as per-file chunks so the
    // public-route bundle ships zero locale data it doesn't use (TRA-115).
    // In test mode, use empty resources (tests should use createTestI18n).
    const instance = i18n.use(LanguageDetector).use(initReactI18next)
    if (!isTestMode) {
        instance.use(createLazyLocaleBackend())
    }
    instance
        .init({
            fallbackLng: parseFallbackLng(),
            supportedLngs: [...SUPPORTED_LANGUAGES],
            load: 'languageOnly',
            // Public-route namespaces are declared up front; their English
            // copies ship statically in the entry (see ./publicResources) so
            // the public routes' LCP never waits on a locale request.
            // Authenticated-only namespaces (admin, lesson, ...) and other
            // languages load on demand through the lazy backend.
            ns: [...PUBLIC_NAMESPACES],
            defaultNS: 'common',
            fallbackNS: ['common'],
            detection: {
                order: ['localStorage', 'navigator', 'querystring', 'htmlTag'],
                caches: ['localStorage'],
            },
            interpolation: {
                escapeValue: false,
            },
            resources: isTestMode ? {} : publicEnResources,
            partialBundledLanguages: true,
        })
        .then(() => {
            // Set initial HTML lang attribute
            updateHtmlLang(i18n.language)

            // Listen for language changes and update HTML lang attribute
            i18n.on('languageChanged', (lng) => {
                updateHtmlLang(lng)
            })
        })
        .catch((error) => {
            console.error('i18n: Initialization failed:', error)
        })
}

export default i18n
